import React, { useState } from "react";

import {
  Menu,
  MenuItem,
  IconButton,
  Toolbar,
  Typography,
  AppBar,
  Box,
  Stack,
  Button,
  SpeedDial,
  SpeedDialAction,
  useMediaQuery,
} from "@mui/material";
import { AccountCircle, Home, Logout, Menu as MenuIcon, Person, QuestionMark, ErrorOutline } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { fetchAllObjects, handleLogout } from "../store/storeFunc";
import { Capitalize, Pluralize } from "../functions/Formatters";
import WelcomeDialog from "./WelcomeDialog";
import GuideDialog from "./GuideDialog";
import FeedbackDialog from "./FeedbackDialog";
import FAQDialog from "./FAQDialog";
import { setWelcomeState } from "./UserManagement/UserActions";
import StyledLink from "./CustomComponents/StyledLink";
import { appName, global } from "../context/global";
import { titleBar } from "../context/titleBar";
import { selectProject } from "./Projects/ProjectActions";

/**
 * TitleBar Component
 *
 * This React component represents the title bar or header of the application. It displays
 * navigation links, a logo, and user-related options, such as user profile and logout.
 * The appearance and behavior of the title bar adapt based on the user's authentication status
 * and screen size.
 *
 * @component
 */
export default function TitleBar() {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Breakpoints to hide components based on viewport size
  const matchesSM = useMediaQuery(theme.breakpoints.up("sm"));

  const auth = useSelector((state) => state.auth);
  const isLoggedIn = auth.account ? true : false;

  const organisations = useSelector((state) => state.organisations.organisations);

  // Handler for welcome dialog
  const handleSetWelcomeState = (open) => {
    setWelcomeState(open, dispatch);
  };

  // State and handlers for guide, feedback, and FAQ dialogs
  const [guideOpen, setGuideOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const [speedDialOpen, setSpeedDialOpen] = useState(false);

  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const fetchSelectedProject = () => {
    if (selectedProject?.id) selectProject(selectedProject.id, dispatch);
  };
  const handleClickRefreshAll = () => {
    fetchAllObjects(dispatch);
    fetchSelectedProject();
  };

  //  State and handlers for navigation menu
  const [anchorElNav, setAnchorElNav] = useState(null);
  const handleOpenNavMenu = (event) => {
    setAnchorElNav(event.currentTarget);
  };
  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  //  State and handlers for user menu
  const [anchorElUser, setAnchorElUser] = useState(null);
  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };
  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Handlers for user menu items
  const handleClickProfile = () => {
    handleCloseUserMenu();
    navigate("/profile");
  };

  const handleClickLogout = () => {
    handleCloseUserMenu();
    handleLogout(dispatch);
  };

  // Array of navigation links. Is defined here so it can be used in both the navigation menu and the title bar, depending on screen size.
  const LinkArr = [
    {
      text: <Home />,
      to: "/",
      requiredRole: isLoggedIn
    },
    {
      text: titleBar.projects,
      to: "/projects",
      requiredRole: isLoggedIn && auth.account.is_org_member,
    },
    {
      text: `${Capitalize(global.my)} ${Pluralize(global.organisation, organisations.length)}`,
      to: "/organisation",
      requiredRole: isLoggedIn && auth.account.is_org_member,
    },
    {
      text: titleBar.admin,
      to: "/admin",
      requiredRole: isLoggedIn && auth.account.is_staff,
    },
    {
      text: titleBar.themes,
      to: "/themes",
      requiredRole: isLoggedIn && !auth.account.is_staff,
    },
  ];

  const speedDialActions = [
    {
      icon: <QuestionMark />,
      name: "Guide",
      onClick: () => {
        setSpeedDialOpen(false);
        setGuideOpen(true);
      },
    },
    {
      icon: <ErrorOutline />,
      name: "Feedback",
      onClick: () => {
        setSpeedDialOpen(false);
        setFeedbackOpen(true);
      },
    },
    {
      icon: <Typography variant="button" sx={{ fontSize: "0.75rem", fontWeight: "bold" }}>FAQ</Typography>,
      name: "FAQ",
      onClick: () => {
        setSpeedDialOpen(false);
        setFaqOpen(true);
      },
    },
  ];

  return (
    <>
      <WelcomeDialog setOpen={() => handleSetWelcomeState(false)} />
      <GuideDialog open={guideOpen} onClose={() => setGuideOpen(false)} />
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <FAQDialog open={faqOpen} onClose={() => setFaqOpen(false)} />
      
      {isLoggedIn && (
        <SpeedDial
          ariaLabel="Help actions"
          sx={{ position: "fixed", bottom: 80, right: "1rem", zIndex: 1000 }}
          icon={<QuestionMark />}
          onClose={() => setSpeedDialOpen(false)}
          onOpen={() => setSpeedDialOpen(true)}
          open={speedDialOpen}
        >
          {speedDialActions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.onClick}
            />
          ))}
        </SpeedDial>
      )}

      {/* <Box> */}
      <Box sx={{ backgroundColor: theme.palette.background.white, width: "100%" }}>
        <Stack
          direction={matchesSM ? "row" : "column"}
          spacing={2}
          justifyContent="space-between"
          alignItems={matchesSM ? "end" : "start"}
          sx={{ marginRight: "28px" }}
        >
          <img
            style={{ marginLeft: "28px", cursor: "pointer" }}
            onClick={() => navigate("/")}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") navigate("/");
            }}
            role="button"
            tabIndex={0}
            src="hwbp-logo.svg"
            alt="HWBP Logo"
            height={80}
          />
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              component={Button}
              onClick={() => {
                isLoggedIn ? handleClickRefreshAll() : navigate("/");
              }}
              // onClick={() => navigate("/")} (original code. I changed it to the above line to make the logo clickable)
              variant="h3"
              sx={{
                // flexGrow: 1,
                // marginRight: "200px",
                textTransform: "none",
                fontWeight: 400,
                fontSize: "2.6rem",
                color: theme.palette.primary.grey,
              }}
            >
              {matchesSM ? appName.full : appName.abbreviated}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 300,
                color: theme.palette.primary.grey,
              }}
            >
              by
            </Typography>
            <img
              src="Haskoning_Logo.png"
              alt="Haskoning Logo"
              height={40}
              style={{ cursor: "pointer" }}
              onClick={() => window.open("https://haskoning.com", "_blank")}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") window.open("https://haskoning.com", "_blank");
              }}
              role="button"
              tabIndex={0}
            />
          </Stack>
        </Stack>
      </Box>

      {/* AppBar */}
      <AppBar
        sx={{
          backgroundColor: theme.palette.primary.main,
          p: 0,
          mb: 2,
          position: "sticky",
          top: "0px",
          zIndex: 1000,
          width: "100%",
        }}
      >
        {/* Toolbar with links and user menu */}
        <Toolbar disableGutters sx={{ width: "100%" }}>
          {isLoggedIn && ( // Only show navigation links if the user is logged in
            <>
              {matchesSM ? ( // If the screen size is large enough, show the links in the title bar. Otherwise, show a menu button that opens a navigation menu.
                <Stack direction="row" spacing={2} alignItems={"center"} sx={{ pl: 4 }}>
                  {LinkArr.filter((item) => item.requiredRole === true).map((item, index) => (
                    <StyledLink key={index} {...item} inMenuItem={false} />
                  ))}
                </Stack>
              ) : (
                <>
                  <IconButton onClick={handleOpenNavMenu}>
                    <MenuIcon fontSize="large" />
                  </IconButton>
                  <Menu
                    id="menu-navigation"
                    anchorEl={anchorElNav}
                    anchorOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: "top",
                      horizontal: "left",
                    }}
                    open={Boolean(anchorElNav)}
                    onClose={handleCloseNavMenu}
                  >
                    {LinkArr.filter((item) => item.requiredRole === true).map((item, index) => (
                      <MenuItem key={index} onClick={handleCloseNavMenu}>
                        <StyledLink {...item} inMenuItem={true} />
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
            </>
          )}

          {/* User menu */}
          {isLoggedIn && (
            <Stack direction="row" spacing={2} alignItems={"center"} sx={{ marginLeft: "auto" }}>
              <IconButton
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleOpenUserMenu}
                color="primary.main.contrastText"
              >
                {matchesSM && (
                  <div style={{ mr: 2 }} direction="row">
                    <Typography color="text.black" variant="body1">
                      {titleBar.loggedInAs} {auth.account.name}
                    </Typography>
                  </div>
                )}
                <AccountCircle sx={{ ml: 2 }} fontSize="large" />
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorElUser}
                anchorOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                keepMounted
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
              >
                <MenuItem onClick={handleClickProfile}>
                  <Person />
                  {titleBar.profile}
                </MenuItem>
                <MenuItem onClick={handleClickLogout}>
                  <Logout />
                  {titleBar.logout}
                </MenuItem>
              </Menu>
            </Stack>
          )}
        </Toolbar>
      </AppBar>
      {/* </Box> */}
    </>
  );
}
